/**
 * usePermissions — one-shot fetch of the current user's effective
 * permissions from GET /api/auth/permissions.
 *
 * Cached at module scope so the endpoint fires ONCE per session
 * (perms don't change on the fly outside of a role edit, which
 * requires a re-login to take effect anyway).
 *
 * Consumers:
 *   const { has, permissions, loading } = usePermissions();
 *   if (!has("clients.create")) return <NoAccess />;
 */
import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

// Module-scope cache. Cleared on logout via the exported reset helper
// so a new user doesn't inherit the previous user's permissions.
let _cache = null;
let _inflight = null;

export function resetPermissionsCache() {
  _cache = null;
  _inflight = null;
}

async function fetchPermissions() {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("no-token");
  if (_inflight) return _inflight;
  _inflight = (async () => {
    const res = await fetch(`${API}/api/auth/permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`permissions ${res.status}`);
    const json = await res.json();
    _cache = new Set(json.permissions || []);
    return _cache;
  })();
  try {
    return await _inflight;
  } finally {
    _inflight = null;
  }
}

export function usePermissions() {
  const [permissions, setPermissions] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_cache) {
      setPermissions(_cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchPermissions()
      .then((set) => { if (!cancelled) { setPermissions(set); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const has = (perm) => Boolean(permissions?.has(perm));
  const hasAny = (...perms) => perms.some((p) => permissions?.has(p));

  return { permissions, loading, error, has, hasAny };
}
